import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/api';
import { formatPriceInput, parsePriceInput } from '@/lib/price-input';
import { cn } from '@/lib/utils';
import { usePriceAnnouncer } from './price-table-announcer';
import { usePriceCellActivity } from './price-cell-activity';

/** Visual state of a cell: plain, derived/grey or missing/orange. */
export type PriceCellState = 'default' | 'muted' | 'warning';

export interface PriceCellProps {
  /** Saved value in wire form ("12.23"); null when the field is empty. */
  value: string | null;
  /** Grey fallback shown in the empty field, e.g. the base price. */
  placeholder?: string | null;
  state?: PriceCellState;
  /** "<product>, <column>" so the field names itself without a visible label. */
  'aria-label': string;
  /** Short state text under the field ("from variants", "missing"). */
  hint?: ReactNode;
  /** Native tooltip of the hint, e.g. the min–max range behind "varies". */
  hintTitle?: string;
  disabled?: boolean;
  /**
   * Saves the parsed draft: a wire string, or null when a previously
   * filled field was emptied. Rejections show a toast and restore the
   * saved value.
   */
  onCommit: (value: string | null) => Promise<void> | void;
  /**
   * Runs before a save, e.g. to ask for confirmation. Returning false keeps
   * the draft in the field and saves nothing.
   */
  onBeforeCommit?: (value: string | null) => Promise<boolean> | boolean;
  /** Rendered right of the field, e.g. the withdraw button of a method. */
  trailing?: ReactNode;
  className?: string;
}

const INVALID_MESSAGE = 'Enter an amount with up to two decimals, e.g. 12.80';
const TOAST_OPTS = { position: 'bottom-right' as const };

const STATE_HINT_CLASS: Record<PriceCellState, string> = {
  default: 'text-mute',
  muted: 'text-mute',
  warning: 'text-warning',
};

/**
 * Editable money cell of the price table (R16). The draft is local:
 * focusing starts an edit, Enter leaves the field, leaving saves when the
 * draft differs from what the field showed on focus, Escape discards. An
 * unreadable draft marks the field invalid, shows the reason under it and
 * keeps the focus. A server value that arrives while the admin is typing
 * is adopted only after the edit ends.
 */
export function PriceCell({
  value,
  placeholder,
  state = 'default',
  'aria-label': ariaLabel,
  hint,
  hintTitle,
  disabled = false,
  onCommit,
  onBeforeCommit,
  trailing,
  className,
}: PriceCellProps) {
  const errorId = useId();
  const announce = usePriceAnnouncer();
  const inputRef = useRef<HTMLInputElement>(null);

  const [draft, setDraft] = useState(() => formatPriceInput(value));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  /** From the end of an edit to the end of its save, confirmation included. */
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The bulk bar waits while any cell is editing, asking for confirmation
  // or saving (R19): its preview was computed on the values before them.
  usePriceCellActivity(editing || committing);

  // Refs mirror the state for handlers that run outside a render (a blur
  // right after Enter, a refetch during a save).
  const editingRef = useRef(false);
  const savingRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  /** What the field showed when the edit began; unchanged drafts save nothing. */
  const baselineRef = useRef(formatPriceInput(value));

  // Adopt a new server value only while nobody is typing or saving.
  useEffect(() => {
    if (!editingRef.current && !savingRef.current) {
      setDraft(formatPriceInput(value));
    }
  }, [value]);

  const beginEditing = () => {
    if (editingRef.current) return;
    editingRef.current = true;
    baselineRef.current = formatPriceInput(valueRef.current);
    setEditing(true);
  };

  const refocus = () => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // Some browsers ignore focus() while the blur is still dispatching.
    if (document.activeElement !== input) {
      setTimeout(() => input.focus(), 0);
    }
  };

  const commit = async () => {
    if (!editingRef.current || savingRef.current) return;

    const parsed = parsePriceInput(draft);
    if (parsed.kind === 'invalid') {
      setError(INVALID_MESSAGE);
      refocus();
      return;
    }

    const next = parsed.kind === 'empty' ? null : parsed.value;
    const baseline = baselineRef.current === '' ? null : baselineRef.current;
    editingRef.current = false;
    setEditing(false);
    setError(null);

    if (next === baseline) {
      // Nothing to save; show whatever the server holds by now.
      setDraft(formatPriceInput(valueRef.current));
      return;
    }

    // Set in the same tick as the edit ends, so the cell stays busy for the
    // bulk bar without a gap while a confirmation dialog is open.
    setCommitting(true);
    try {
      if (onBeforeCommit && !(await onBeforeCommit(next))) {
        // Cancelled: the draft stays in the field for another try.
        return;
      }

      savingRef.current = true;
      setSaving(true);
      try {
        await onCommit(next);
        setDraft(next ?? '');
        announce('Saved');
      } catch (cause) {
        const message = getErrorMessage(cause);
        toast.error(message, TOAST_OPTS);
        announce(`Not saved: ${message}`);
        setDraft(formatPriceInput(valueRef.current));
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    } finally {
      setCommitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      editingRef.current = false;
      setEditing(false);
      setError(null);
      setDraft(formatPriceInput(valueRef.current));
      event.currentTarget.blur();
    }
  };

  const showHint = !error && hint !== undefined && hint !== null;

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <div className="flex items-center gap-0.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={draft}
          placeholder={placeholder ?? undefined}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          aria-busy={saving || undefined}
          disabled={disabled || saving}
          data-editing={editing || undefined}
          onFocus={beginEditing}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={handleKeyDown}
          className={cn(
            'h-8 w-[4.5rem] rounded-control bg-paper px-1.5 text-right font-mono text-sm text-ink ring-1 ring-inset ring-line transition-[box-shadow] duration-[120ms] placeholder:text-mute',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            'disabled:cursor-not-allowed disabled:opacity-40',
            state === 'warning' && 'ring-warning placeholder:text-warning',
            error && 'ring-danger'
          )}
        />
        {trailing}
      </div>
      {error && (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      )}
      {showHint && (
        <span
          className={cn('text-[11px] leading-tight', STATE_HINT_CLASS[state])}
          title={hintTitle}
        >
          {hint}
        </span>
      )}
    </div>
  );
}
